const round1 = (value) => Math.round(Number(value) * 10) / 10

const cache = new WeakMap()

export function normalizeSrc(src) {
    if (!src) return ''
    try {
        const url = new URL(src, window.location.href)
        url.hash = ''
        return url.href
    } catch (error) {
        return String(src)
    }
}

const backgroundSrc = (el) => {
    const value = el.ownerDocument.defaultView.getComputedStyle(el).backgroundImage
    if (!value || value === 'none') return ''
    const matched = value.match(/url\((['"]?)(.*?)\1\)/)
    if (!matched) return ''
    return normalizeSrc(matched[2])
}

const measure = (el) => {
    const box = el.getBoundingClientRect()
    if (box.width < 8 || box.height < 8) return null
    const style = el.ownerDocument.defaultView.getComputedStyle(el)
    if (style.display === 'none' || style.visibility === 'hidden') return null
    return box
}

const cacheFor = (doc) => {
    let map = cache.get(doc)
    if (!map) {
        map = new Map()
        cache.set(doc, map)
    }
    return map
}

const groupsOf = (doc) => {
    const images = new Map()
    ;[...doc.images].forEach((img) => {
        if (img.closest('#kooyii-hotspot-layer, .page-hotspot-layer')) return
        const src = normalizeSrc(img.currentSrc || img.src)
        if (!src) return
        if (!images.has(src)) images.set(src, [])
        images.get(src).push(img)
    })

    const backgrounds = new Map()
    doc.body.querySelectorAll('*').forEach((el) => {
        if (el.closest('#kooyii-hotspot-layer, .page-hotspot-layer')) return
        const src = backgroundSrc(el)
        if (!src) return
        if (!backgrounds.has(src)) backgrounds.set(src, [])
        backgrounds.get(src).push(el)
    })
    return { images, backgrounds }
}

export function findAnchorElement(doc, hotspot) {
    if (!hotspot?.anchor_src || !hotspot.anchor_type) return null
    const key = `${hotspot.anchor_type}|${hotspot.anchor_src}|${hotspot.anchor_index || 0}`
    const saved = cacheFor(doc)
    const previous = saved.get(key)
    if (previous?.isConnected) return previous
    const { images, backgrounds } = groupsOf(doc)
    const list = hotspot.anchor_type === 'bg' ? backgrounds.get(hotspot.anchor_src) : images.get(hotspot.anchor_src)
    const element = list?.[Number(hotspot.anchor_index) || 0] || null
    if (element) saved.set(key, element)
    return element
}

const intersectionArea = (rect, box) => {
    const width = Math.min(rect.right, box.right) - Math.max(rect.left, box.left)
    const height = Math.min(rect.bottom, box.bottom) - Math.max(rect.top, box.top)
    if (width <= 0 || height <= 0) return 0
    return width * height
}

export function anchorFromPageRect(doc, layer, pageRect) {
    const layerBox = layer.getBoundingClientRect()
    const plain = {
        x: round1(pageRect.x),
        y: round1(pageRect.y),
        width: round1(pageRect.width),
        height: round1(pageRect.height),
        anchor_type: '',
        anchor_src: '',
        anchor_index: 0,
    }
    if (!layerBox.width || !layerBox.height) return plain

    const rect = {
        left: layerBox.left + (pageRect.x / 100) * layerBox.width,
        top: layerBox.top + (pageRect.y / 100) * layerBox.height,
        width: (pageRect.width / 100) * layerBox.width,
        height: (pageRect.height / 100) * layerBox.height,
    }
    rect.right = rect.left + rect.width
    rect.bottom = rect.top + rect.height
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2
    const { images, backgrounds } = groupsOf(doc)
    let best = null

    const consider = (groups, type) => {
        groups.forEach((list, src) => {
            list.forEach((el, index) => {
                const box = measure(el)
                if (!box) return
                const containsCenter = centerX >= box.left && centerX <= box.right && centerY >= box.top && centerY <= box.bottom
                const overlap = intersectionArea(rect, box)
                if (!containsCenter && overlap <= 0) return
                const area = box.width * box.height
                const candidate = { type, src, index, box, containsCenter, area, overlap }
                if (!best || (candidate.containsCenter && !best.containsCenter)) {
                    best = candidate
                    return
                }
                if (candidate.containsCenter !== best.containsCenter) return
                if (candidate.containsCenter && candidate.area < best.area) best = candidate
                if (!candidate.containsCenter && candidate.overlap > best.overlap) best = candidate
            })
        })
    }

    consider(images, 'img')
    consider(backgrounds, 'bg')

    const rectArea = rect.width * rect.height
    if (!best || (!best.containsCenter && best.overlap < rectArea * 0.5)) return plain

    return {
        x: round1((rect.left - best.box.left) / best.box.width * 100),
        y: round1((rect.top - best.box.top) / best.box.height * 100),
        width: round1(rect.width / best.box.width * 100),
        height: round1(rect.height / best.box.height * 100),
        anchor_type: best.type,
        anchor_src: best.src,
        anchor_index: best.index,
    }
}

export function pageRectFromHotspot(doc, layer, hotspot) {
    if (!hotspot?.anchor_src || !hotspot.anchor_type) {
        return {
            x: Number(hotspot?.x) || 0,
            y: Number(hotspot?.y) || 0,
            width: Number(hotspot?.width) || 0,
            height: Number(hotspot?.height) || 0,
        }
    }
    const element = findAnchorElement(doc, hotspot)
    const box = element && measure(element)
    const layerBox = layer.getBoundingClientRect()
    if (!box || !layerBox.width || !layerBox.height) return null
    const left = box.left - layerBox.left + (hotspot.x / 100) * box.width
    const top = box.top - layerBox.top + (hotspot.y / 100) * box.height
    return {
        x: left / layerBox.width * 100,
        y: top / layerBox.height * 100,
        width: (hotspot.width / 100 * box.width) / layerBox.width * 100,
        height: (hotspot.height / 100 * box.height) / layerBox.height * 100,
    }
}
